import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Clock, ArrowLeft } from 'lucide-react';
import { dynamicQrService } from '../services/dynamicQr';
import { benefitsService } from '../services/benefits';
import { savingsService } from '../services/savings';
import { CanjeFelicidades } from './CanjeFelicidades';

function fmt(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/**
 * `ACTIVACION DE BENEFICIO` — el QR del cliente.
 *
 * Sigue el mockup al pie de la letra: fondo en degradado de marca, el titular
 * en blanco, y una tarjeta blanca con el aliado, el beneficio, el código y el
 * tiempo que le queda.
 *
 * Antes esta pantalla tenía una guía de tres pasos. Se quitó: se construyó
 * cuando al QR se llegaba sin contexto, y hoy se llega justo después de elegir
 * el beneficio. Explicar tres pasos en ese momento sobra, y el parámetro
 * visual es el de Iván.
 *
 * Por lo mismo el código **se genera solo** al entrar: el usuario ya dijo lo
 * que quería en la pantalla anterior; pedirle otro botón sería un trámite.
 */
/*
  `soloVista` = el visitante sin cuenta.

  Iván: que pueda entrar y ver qué hay, con las funciones apagadas, en vez de
  toparse con un candado que lo rebota. Ve la pantalla real —el degradado, la
  tarjeta, dónde aparecerá su código— y el botón, en tono apagado, lo lleva a
  activar su beneficio, que es lo único que le falta.

  No se piden datos en este modo: sin sesión, las tres llamadas responderían 401
  y la pantalla acabaría enseñando un error en vez de una promesa.
*/
const MyQR = ({ soloVista = false }) => {
  const navigate = useNavigate();
  const [qrData, setQrData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [caducado, setCaducado] = useState(false);
  /* El beneficio que se está mostrando: da nombre y descripción a la tarjeta. */
  const [beneficio, setBeneficio] = useState(null);
  const [esSuscriptor, setEsSuscriptor] = useState(false);
  const [sinBeneficio, setSinBeneficio] = useState(false);
  /* Canje detectado mientras el QR estaba en pantalla. */
  const [canjeReciente, setCanjeReciente] = useState(null);
  /* Foto del "antes" contra la que se decide si un canje es nuevo. Tres
     estados, y la diferencia entre los dos primeros es justo lo que fallaba:
     `undefined` = todavía no se pudo tomar, no hay con qué comparar;
     `null` = se tomó y el cliente no tenía ningún canje; un id = el que ya
     tenía antes de que se emitiera este código. */
  const ultimoCanjePrevio = useRef(undefined);
  const yaGenero = useRef(false);

  const handleExpiration = useCallback(async () => {
    if (qrData?.token) {
      try { await dynamicQrService.expire(qrData.token); } catch (_) {}
    }
    setQrData(null);
    setTimeLeft(0);
    setCaducado(true);
  }, [qrData?.token]);

  /* Cuenta regresiva */
  useEffect(() => {
    if (!qrData?.expiresAt) return undefined;
    const timer = setInterval(() => {
      const left = Math.max(0, Math.floor((new Date(qrData.expiresAt) - new Date()) / 1000));
      if (left <= 0) handleExpiration();
      else setTimeLeft(left);
    }, 1000);
    return () => clearInterval(timer);
  }, [qrData?.expiresAt, handleExpiration]);

  /*
    Foto del "antes": contra esto se compara para saber si la tienda escaneó.

    Si la lectura falla se deja en `undefined`, no en `null`. Dar por hecho que
    no había canjes es lo que hacía que el primer sondeo tomara una compra vieja
    por nueva y felicitara al cliente sin que nadie hubiera cobrado.
  */
  const tomarFotoPrevia = useCallback(async () => {
    try {
      const previo = await savingsService.last();
      ultimoCanjePrevio.current = previo?.data?.id ?? null;
    } catch (_) {
      ultimoCanjePrevio.current = undefined;
    }
  }, []);

  const generarQR = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      await tomarFotoPrevia();
      const res = await dynamicQrService.generate();
      if (res.success && res.data) {
        const { token, expires_at } = res.data;
        setQrData({ token, expiresAt: new Date(expires_at) });
        setTimeLeft(Math.max(0, Math.floor((new Date(expires_at) - new Date()) / 1000)));
        setCaducado(false);
      } else {
        throw new Error(res.error || 'No se pudo generar tu código');
      }
    } catch (err) {
      setError(err.message || 'No se pudo generar tu código');
    } finally {
      setGenerating(false);
    }
  }, [tomarFotoPrevia]);

  /* Carga inicial: qué beneficio lleva, y su código listo. */
  useEffect(() => {
    if (soloVista) { setLoading(false); return undefined; }
    let vigente = true;
    (async () => {
      try {
        const [estado, catalogo, actual] = await Promise.all([
          benefitsService.getUnlockState(),
          benefitsService.getAll().catch(() => null),
          dynamicQrService.getStatus().catch(() => null),
        ]);
        if (!vigente) return;

        const suscriptor = Boolean(estado?.isSubscriber);
        setEsSuscriptor(suscriptor);

        const todos = catalogo?.data ?? [];
        const abiertos = estado?.unlockedBenefitIds ?? [];
        const propios = suscriptor ? todos : todos.filter((b) => abiertos.includes(String(b.id)));
        setBeneficio(propios[0] ?? null);
        setSinBeneficio(!suscriptor && propios.length === 0);

        if (actual?.success && actual.has_active_qr && actual.data) {
          // Ya tenía un código vivo: se reusa en vez de emitir otro. La foto
          // del "antes" hay que tomarla igual — este camino no pasa por
          // generarQR, y sin ella el sondeo no tenía contra qué comparar.
          await tomarFotoPrevia();
          const { token, expires_at } = actual.data;
          setQrData({ token, expiresAt: new Date(expires_at) });
          setTimeLeft(Math.max(0, Math.floor((new Date(expires_at) - new Date()) / 1000)));
          yaGenero.current = true;
        } else if (suscriptor || abiertos.length > 0) {
          yaGenero.current = true;
          await generarQR();
        }
      } catch (err) {
        if (vigente) setError(err.message || 'No se pudo cargar tu código');
      } finally {
        if (vigente) setLoading(false);
      }
    })();
    return () => { vigente = false; };
  }, [generarQR, tomarFotoPrevia, soloVista]);

  /*
    Sondeo: cómo se entera el teléfono de que la tienda ya escaneó.

    El cliente está en la caja con el código en pantalla y su teléfono no sabe
    lo que pasó del otro lado del mostrador. Mientras el código está a la vista
    se pregunta cada 3 segundos por su último canje, y se compara contra el que
    existía ANTES de emitirlo — no contra "¿hay alguno?", porque quien ya compró
    otras veces siempre tendría uno.
  */
  useEffect(() => {
    if (!qrData?.token) return undefined;
    let vigente = true;
    const intervalo = setInterval(async () => {
      try {
        const res = await savingsService.last();
        if (!vigente) return;
        const ultimo = res?.data ?? null;
        const idUltimo = ultimo?.id ?? null;

        /* Sin foto del "antes" no se puede distinguir un canje nuevo de una
           compra anterior. Se toma ahora y se compara a partir del siguiente
           tick, en vez de arriesgar una felicitación falsa. */
        if (ultimoCanjePrevio.current === undefined) {
          ultimoCanjePrevio.current = idUltimo;
          return;
        }
        if (idUltimo === null || idUltimo === ultimoCanjePrevio.current) return;

        clearInterval(intervalo);
        setCanjeReciente(ultimo);
        setQrData(null);
      } catch (_) {
        /* Un fallo de red suelto no debe cortar el sondeo. */
      }
    }, 3000);
    return () => { vigente = false; clearInterval(intervalo); };
  }, [qrData?.token]);

  const cerrarFelicidades = () => {
    setCanjeReciente(null);
    navigate('/prepago');
  };

  const descripcion = beneficio
    ? [beneficio.headlineNumber, beneficio.headlineThin, beneficio.headlineStrong, beneficio.scope]
        .filter(Boolean)
        .join(' ') || beneficio.title
    : '';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #F73E48 0%, #F52B16 45%, #FA759F 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 'max(58px, env(safe-area-inset-top, 58px)) 24px 130px',
      }}
    >
      {canjeReciente && <CanjeFelicidades canje={canjeReciente} onCerrar={cerrarFelicidades} />}

      {/*
        Salida siempre visible.

        La pantalla ocupa todo y va en degradado, así que se lee como un modal:
        Iván dijo que se sentía atrapado esperando a que la tienda validara.
        La barra de navegación seguía ahí abajo, pero no basta con que exista —
        tiene que verse una salida en la pantalla donde estás.
      */}
      <button
        type="button"
        onClick={() => navigate('/home')}
        aria-label="Volver a mis beneficios"
        style={{
          position: 'absolute',
          top: 'max(18px, env(safe-area-inset-top, 18px))',
          left: 18,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 38,
          padding: '0 14px 0 10px',
          borderRadius: 9999,
          background: 'rgba(255,255,255,0.18)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.35)',
          color: '#fff',
          fontSize: 13.5,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <ArrowLeft size={16} />
        Volver
      </button>

      <h1
        className="font-display"
        style={{
          color: '#fff',
          fontSize: 'clamp(26px, 7.6vw, 34px)',
          fontWeight: 700,
          letterSpacing: '-0.025em',
          textAlign: 'center',
          margin: 0,
          lineHeight: 1.15,
        }}
      >
        {soloVista
          ? 'Aquí va tu código'
          : sinBeneficio ? 'Elige tu beneficio' : caducado ? 'Tu código caducó' : '¡Tu beneficio está listo!'}
      </h1>

      <p
        style={{
          color: 'rgba(255,255,255,0.92)',
          fontSize: 15,
          textAlign: 'center',
          margin: '10px 0 0',
          lineHeight: 1.45,
          maxWidth: 320,
        }}
      >
        {soloVista
          ? 'Así se ve el código que le enseñas al cajero. Activa tu beneficio para tener el tuyo.'
          : sinBeneficio
          ? 'Todavía no tienes ninguno activado. Es gratis y toma un momento.'
          : caducado
            ? 'Tu beneficio sigue intacto. Genera otro código cuando estés en caja.'
            : 'Muéstralo en caja para obtener tu descuento'}
      </p>

      {/* La tarjeta blanca del mockup */}
      <div
        style={{
          background: '#fff',
          borderRadius: 30,
          width: '100%',
          maxWidth: 360,
          marginTop: 34,
          padding: '30px 24px 22px',
          boxShadow: '0 22px 50px -18px rgba(0,0,0,0.3)',
          textAlign: 'center',
        }}
      >
        {soloVista ? (
          <>
            {/* El hueco del código, apagado: enseña la forma sin fingir un QR
                que no existe. */}
            <div
              style={{
                width: 216,
                height: 216,
                margin: '8px auto 18px',
                borderRadius: 18,
                background: 'repeating-linear-gradient(45deg, #F5F5F7, #F5F5F7 10px, #ECECEF 10px, #ECECEF 20px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ color: '#A6A6AD', fontSize: 13, textAlign: 'center', padding: '0 24px' }}>
                Tu código aparece aquí
              </span>
            </div>
            <button
              type="button"
              onClick={() => navigate('/beneficios/elegir')}
              className="rounded-full font-semibold"
              style={{
                width: '100%',
                height: 52,
                border: 'none',
                fontSize: 15,
                cursor: 'pointer',
                /* Apagado a propósito: se ve que existe y que todavía no es
                   suyo. Al tocarlo lleva a activarlo, no a un aviso. */
                background: '#F0D9D4',
                color: '#B4675C',
              }}
            >
              Activar mi beneficio para usarlo
            </button>
          </>
        ) : loading ? (
          <p style={{ color: '#6E6E77', fontSize: 14, margin: '40px 0' }}>Preparando tu código...</p>
        ) : sinBeneficio ? (
          <>
            <p style={{ color: '#6E6E77', fontSize: 14.5, lineHeight: 1.5, margin: '10px 0 22px' }}>
              Escoge el beneficio que quieres estrenar y aquí aparecerá tu código.
            </p>
            <button
              type="button"
              onClick={() => navigate('/beneficios/elegir')}
              className="bg-grad-vivo text-white rounded-full font-semibold"
              style={{ width: '100%', height: 52, border: 'none', fontSize: 15, cursor: 'pointer' }}
            >
              Elegir mi beneficio
            </button>
          </>
        ) : (
          <>
            {/*
              El código del socio NO es de una tienda.

              Antes se pintaba `propios[0].storeName`, que es simplemente el
              primer beneficio del catálogo: a quien había pagado le aparecía
              «Tous» y parecía que su acceso servía solo ahí. Iván lo reportó
              tal cual. El QR siempre fue general —comprobado canjeando el mismo
              código en Nike y en Tous—; lo que mentía era esta pantalla.

              Quien no paga sí ve su tienda, y debe verla: su código abre
              únicamente el beneficio que desbloqueó.

              Al socio ya no se le pone titular. Decía «Todas las tiendas» y Iván
              pidió quitarlo (13-ago-2026): un titular con nombre de tienda invita
              a leerlo como restricción, y su acceso no restringe nada. El párrafo
              de abajo ya explica qué es el código, sin prometer un alcance en
              dos palabras.
            */}
            {!esSuscriptor && (
              <div
                className="font-display"
                style={{
                  fontSize: 'clamp(26px, 8vw, 34px)',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: '#0E0E10',
                  lineHeight: 1.1,
                }}
              >
                {beneficio?.storeName || 'Tu beneficio'}
              </div>
            )}

            {esSuscriptor ? (
              <p style={{ fontSize: 14, color: '#33333A', margin: '12px 0 0', lineHeight: 1.4 }}>
                Tu acceso vondi. Muéstralo en cualquier tienda aliada y elige ahí el beneficio que quieras usar.
              </p>
            ) : (
              descripcion && (
                <p style={{ fontSize: 14, color: '#33333A', margin: '12px 0 0', lineHeight: 1.4 }}>
                  {descripcion}
                </p>
              )
            )}

            {qrData ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'center', margin: '26px 0 20px' }}>
                  <QRCodeSVG value={qrData.token} size={216} level="H" includeMargin={false} />
                </div>

                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    fontSize: 14,
                    color: timeLeft < 60 ? '#E23434' : '#0E0E10',
                    fontWeight: 600,
                  }}
                >
                  <Clock size={16} />
                  <span>
                    válido por{' '}
                    <span style={{ color: timeLeft < 60 ? '#E23434' : '#6E6E77', fontWeight: 500 }}>
                      {fmt(timeLeft)} min
                    </span>
                  </span>
                </div>
              </>
            ) : (
              <button
                type="button"
                onClick={generarQR}
                disabled={generating}
                className="bg-grad-vivo text-white rounded-full font-semibold"
                style={{
                  width: '100%',
                  height: 52,
                  border: 'none',
                  fontSize: 15,
                  marginTop: 24,
                  cursor: generating ? 'not-allowed' : 'pointer',
                  opacity: generating ? 0.7 : 1,
                }}
              >
                {generating ? 'Generando...' : 'Generar otro código'}
              </button>
            )}
          </>
        )}

        {error && (
          <p style={{ color: '#9C2A1F', fontSize: 13, marginTop: 16, lineHeight: 1.45 }}>{error}</p>
        )}
      </div>
    </div>
  );
};

export default MyQR;
